import { forwardRef, useId, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

export type FormFieldKind =
  | 'text'
  | 'number'
  | 'date'
  | 'select'
  | 'textarea'
  | 'checkbox'
  | 'combobox';

interface BaseProps {
  label: string;
  hint?: string;
  error?: string;
  required?: boolean;
  className?: string;
}

interface TextLikeProps extends BaseProps {
  kind: 'text' | 'number' | 'date';
  value?: string | number;
  defaultValue?: string | number;
  onChange?: (v: string) => void;
  placeholder?: string;
  name?: string;
  disabled?: boolean;
  autoComplete?: string;
  min?: number | string;
  max?: number | string;
  step?: number | string;
}

interface SelectProps extends BaseProps {
  kind: 'select';
  value?: string;
  defaultValue?: string;
  onChange?: (v: string) => void;
  options: { value: string; label: string }[];
  name?: string;
  disabled?: boolean;
  placeholder?: string;
}

interface TextareaProps extends BaseProps {
  kind: 'textarea';
  value?: string;
  defaultValue?: string;
  onChange?: (v: string) => void;
  rows?: number;
  name?: string;
  placeholder?: string;
  disabled?: boolean;
}

interface CheckboxProps extends BaseProps {
  kind: 'checkbox';
  checked?: boolean;
  defaultChecked?: boolean;
  onChange?: (v: boolean) => void;
  name?: string;
  disabled?: boolean;
}

interface ComboboxSlotProps extends BaseProps {
  kind: 'combobox';
  children: ReactNode;
}

export type FormFieldProps =
  | TextLikeProps
  | SelectProps
  | TextareaProps
  | CheckboxProps
  | ComboboxSlotProps;

const inputClasses = cn(
  'block w-full rounded-md border bg-neutral-0 px-12 text-base text-neutral-900',
  'h-11 laptop:h-9',
  'border-neutral-200 placeholder:text-neutral-400',
  'focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500',
  'disabled:bg-neutral-50 disabled:text-neutral-400',
);

export const FormField = forwardRef<HTMLDivElement, FormFieldProps>(function FormField(
  props,
  ref,
) {
  const id = useId();
  const { label, hint, error, required, className } = props;
  const labelEl = (
    <label htmlFor={id} className="block text-sm font-medium text-neutral-700">
      {label}
      {required && <span className="ml-2 text-error-500">*</span>}
    </label>
  );
  const helper = error ? (
    <p className="text-xs text-error-500" role="alert">
      {error}
    </p>
  ) : hint ? (
    <p className="text-xs text-neutral-500">{hint}</p>
  ) : null;

  let control: ReactNode;
  switch (props.kind) {
    case 'text':
    case 'number':
    case 'date': {
      const { value, defaultValue, onChange, placeholder, name, disabled, autoComplete, min, max, step } =
        props;
      control = (
        <input
          id={id}
          name={name}
          type={props.kind}
          value={value as string | number | undefined}
          defaultValue={defaultValue as string | number | undefined}
          onChange={onChange ? (e) => onChange(e.target.value) : undefined}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete={autoComplete}
          min={min}
          max={max}
          step={step}
          aria-invalid={error ? true : undefined}
          className={inputClasses}
        />
      );
      break;
    }
    case 'select': {
      const { value, defaultValue, onChange, options, name, disabled, placeholder } = props;
      control = (
        <select
          id={id}
          name={name}
          value={value}
          defaultValue={defaultValue}
          onChange={onChange ? (e) => onChange(e.target.value) : undefined}
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          className={inputClasses}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      );
      break;
    }
    case 'textarea': {
      const { value, defaultValue, onChange, rows = 4, name, placeholder, disabled } = props;
      control = (
        <textarea
          id={id}
          name={name}
          rows={rows}
          value={value}
          defaultValue={defaultValue}
          placeholder={placeholder}
          disabled={disabled}
          onChange={onChange ? (e) => onChange(e.target.value) : undefined}
          aria-invalid={error ? true : undefined}
          className={cn(
            'block w-full rounded-md border bg-neutral-0 px-12 py-8 text-base text-neutral-900',
            'border-neutral-200 placeholder:text-neutral-400',
            'focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500',
          )}
        />
      );
      break;
    }
    case 'checkbox': {
      const { checked, defaultChecked, onChange, name, disabled } = props;
      return (
        <div ref={ref} className={cn('flex items-start gap-12', className)}>
          <input
            id={id}
            name={name}
            type="checkbox"
            checked={checked}
            defaultChecked={defaultChecked}
            disabled={disabled}
            onChange={onChange ? (e) => onChange(e.target.checked) : undefined}
            className="mt-4 h-20 w-20 rounded-sm border-neutral-300 text-primary-500 focus:ring-primary-500"
          />
          <div className="flex flex-col gap-2">
            <label htmlFor={id} className="text-sm font-medium text-neutral-700">
              {label}
              {required && <span className="ml-2 text-error-500">*</span>}
            </label>
            {helper}
          </div>
        </div>
      );
    }
    case 'combobox': {
      control = props.children;
      break;
    }
  }

  return (
    <div ref={ref} className={cn('flex flex-col gap-6', className)}>
      {labelEl}
      {control}
      {helper}
    </div>
  );
});
